import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { authService } from '../services/api';
import { prefetchService } from '../services/prefetchService';

interface AuthContextType extends AuthState {
  login: (email: string, password: string, role?: string) => Promise<void>;
  register: (email: string, password: string, role: string) => Promise<void>;
  logout: () => Promise<void>;
}

const defaultAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
};

const AuthContext = createContext<AuthContextType>({
  ...defaultAuthState,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          setAuthState({
            isAuthenticated: true,
            user,
            loading: false,
            error: null,
          });
          
          // Prefetch data based on user role
          prefetchService.prefetchDataForUser(user);
        } else {
          setAuthState({
            ...defaultAuthState,
            loading: false,
          });
        }
      } catch (error) {
        setAuthState({
          ...defaultAuthState,
          loading: false,
          error: 'Failed to check authentication status',
        });
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string, role?: string) => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      const response = await authService.login(email, password);

      const userData = response;

      // Extract first name and last name from email or fetch from API
      let fname = '';
      let lname = '';
      let organization = '';
      
      // If the email contains a dot, assume it's in the format firstname.lastname@domain.com
      if (email.includes('@')) {
        const localPart = email.split('@')[0];
        if (localPart.includes('.')) {
          const nameParts = localPart.split('.');
          fname = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
          lname = nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
        } else {
          // If no dot in the local part, use the whole local part as first name
          fname = localPart.charAt(0).toUpperCase() + localPart.slice(1);
          lname = '';
        }
        
        // For payers, extract organization from email domain
        if (userData.role === 'payer' && email.includes('@')) {
          const domain = email.split('@')[1];
          organization = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
        }
      }

      const user: User = {
        id: userData.id || '',
        username: email,
        fname: fname,
        lname: lname,
        role: (userData.role as 'patient' | 'provider' | 'superuser' | 'payer') || 'patient',
        organization: organization || undefined,
      };

      localStorage.setItem('user', JSON.stringify(user));
      setAuthState({
        isAuthenticated: true,
        user,
        loading: false,
        error: null,
      });

      // Prefetch data based on user role
      prefetchService.prefetchDataForUser(user);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  const register = async (email: string, password: string, role: string) => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      await authService.register(email, password, role);

      setAuthState((prev) => ({
        ...prev,
        loading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  const logout = async () => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      await authService.logout();
      localStorage.removeItem('user');

      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
